import { useT } from "@transifex/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { StudentTestAnswer } from "p6m-p6u";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { GlobeSimpleIcon } from "@phosphor-icons/react";
import IconButton from "../../basic/iconButton/IconButton";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import ToastMessage from "../../basic/toastMessage/ToastMessage";
import ClassActivitiesSection from "../../complex/schoolClasses/classDetails/ClassActivitiesSection";
import ClassAccessSection from "../../complex/schoolClasses/classDetails/ClassAccessSection";
import ShareClassAccessPopup from "../../complex/schoolClasses/classDetails/ShareClassAccessPopup";
import ShareClassMemberAccessPopup, {
    ClassMemberCopyKind,
} from "../../complex/schoolClasses/classDetails/ShareClassMemberAccessPopup";
import ClassMembersSection from "../../complex/schoolClasses/classDetails/ClassMembersSection";
import AddClassMembersPopup from "../../complex/schoolClasses/classDetails/AddClassMembersPopup";
import EditClassPopup from "../../complex/schoolClasses/popups/EditClassPopup";
import DeleteClassPopup from "../../complex/schoolClasses/popups/DeleteClassPopup";
import EditClassMemberPopup from "../../complex/schoolClasses/popups/EditClassMemberPopup";
import DeleteClassMemberPopup from "../../complex/schoolClasses/popups/DeleteClassMemberPopup";
import ReviewDuplicatesPopup from "../../complex/schoolClasses/popups/ReviewDuplicatesPopup";
import { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { isPrivateSchoolClass } from "../../utils/schoolClassUtils";
import { ToastVariants } from "../../enums/toast";
import { toClassVisibilityProp } from "../../helpers/analyticsEventProps";
import useBreakpoints from "../../hooks/useBreakpoints";
import { useLogger } from "../../hooks/useLogger";
import useSchoolClassPage from "../../hooks/useSchoolClassPage";
import {
    EXIT_PREVIEW_PARAM,
    getClassDetailsRoute,
    getDownloadCodesRoute,
    getSchoolClassesRoute,
    isExitPreviewSearch,
} from "../../helpers/navigationHelper";
import { clearClassMemberSession, readClassMemberSession } from "../../context/classMemberStorage";
import useToast from "../../hooks/useToast";
import { useGeneralContext } from "../../context/AppGeneralContext";
import { useRTContext } from "../../context/RTContext";
import SchoolClassPageLayout from "./layout/SchoolClassPageLayout";
import {
    addMembersToClass,
    deleteClassMember,
    getMembersForClass,
    updateClassMember,
    type ClassMember,
} from "../../networking/schoolClasses";
import {
    createClassMemberTestResult,
    getTestResultsForTest,
    getUserTests,
    saveStudentTestResult,
} from "../../networking/tests";
import useNewClassMembers from "../../hooks/useNewClassMembers";
import {
    dismissDuplicateGroup,
    duplicateGroupKey,
    findDuplicateGroups,
    readDismissedDuplicateKeys,
} from "../../helpers/ClassMemberDuplicates";
import { executeClassMemberMerge, MergeConflictStrategy, planClassMemberMerge } from "../../helpers/ClassMemberMerge";

enum OpenPopup {
    NONE,
    SHARE,
    EDIT,
    DELETE,
    ADD_MEMBERS,
}

const ANALYTICS_METHOD_BY_COPY_KIND: Record<ClassMemberCopyKind, string> = {
    [ClassMemberCopyKind.CLASS_CODE]: "class_code",
    [ClassMemberCopyKind.PERSONAL_CODE]: "member_code",
    [ClassMemberCopyKind.DIRECT_LINK]: "member_link",
};

interface MergeReview {
    members: ClassMember[];
    source: "duplicate_group" | "selection";
}

const ClassDetails = () => {
    const history = useHistory();
    const { search } = useLocation();
    const t = useT();
    const { schoolClassId } = useParams<{ schoolClassId: string }>();
    const { isAboveMD: isDesktop } = useBreakpoints();
    const { schoolClasses, updateSchoolClass, deleteSchoolClass, cleanupPrivateToPublic } = useRTContext();
    const { setIsDataBeingLoaded } = useGeneralContext();
    const { logEvent } = useLogger();

    const [openPopup, setOpenPopup] = useState(OpenPopup.NONE);
    // null = not loaded yet (initial, or invalidated by a visibility flip); [] = confirmed empty.
    const [classMembers, setClassMembers] = useState<ClassMember[] | null>(null);
    const [classMembersLoadFailed, setClassMembersLoadFailed] = useState(false);
    const [codeCopiedMessage, setCodeCopiedMessage] = useState("");
    const [editingMember, setEditingMember] = useState<ClassMember | null>(null);
    const [deletingMembers, setDeletingMembers] = useState<ClassMember[] | null>(null);
    const [sharingMember, setSharingMember] = useState<ClassMember | null>(null);
    const [dismissedDuplicateKeys, setDismissedDuplicateKeys] = useState<string[]>(() =>
        readDismissedDuplicateKeys(schoolClassId)
    );
    const [review, setReview] = useState<MergeReview | null>(null);
    // Results fetched by the conflict check, reused by a merge that follows at once. After a conflict
    // step the teacher had time to think, so the merge refetches rather than delete what came in meanwhile.
    const mergeResultsRef = useRef<{
        key: string;
        resultsByTest: Record<string, StudentTestAnswer[]>;
        hasConflicts: boolean;
    } | null>(null);

    useEffect(() => {
        setDismissedDuplicateKeys(readDismissedDuplicateKeys(schoolClassId));
    }, [schoolClassId]);

    const linkCopiedToast = useToast();
    const editSuccessToast = useToast();
    const classMembersAddedToast = useToast();
    const classMembersAddErrorToast = useToast();
    const classMembersLoadErrorToast = useToast();
    const classMemberEditedToast = useToast();
    const classMemberDeletedToast = useToast();
    const classMembersDeletedToast = useToast();
    const classMemberActionErrorToast = useToast();
    const codeCopiedToast = useToast();
    const classMembersMergedToast = useToast();

    const { isLoaded, schoolClass } = useSchoolClassPage(
        `${getClassDetailsRoute(schoolClassId)}${search}`,
        schoolClassId
    );

    const t_backToClasses = t("Back to classes", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_sr_edit = t("Edit class", {
        _tags: "SchoolClasses, ClassDetails, ScreenReaderOnly",
    });
    const t_sr_delete = t("Delete class", {
        _tags: "SchoolClasses, ClassDetails, ScreenReaderOnly",
    });
    const t_editSuccess = t("Changes saved.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_linkCopied = t("Link copied \u2013 you can paste it with Ctrl+V/Cmd+V.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMembersAdded = t("Students added successfully!", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMembersAddError = t("Creating some students failed. Please try again later.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMembersLoadError = t("Could not load students. Please try again later.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMemberEdited = t("Student updated successfully!", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMemberDeleted = t("Student deleted successfully!", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMembersDeleted = t("Students deleted successfully!", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMemberActionError = t("Something went wrong. Please try again later.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classMembersMerged = t("Entries merged.", { _tags: "SchoolClasses, ClassDetails" });
    const t_public = t("Public", { _tags: "SchoolClasses, ClassDetails" });
    const t_private = t("Private", { _tags: "SchoolClasses, ClassDetails" });
    const t_publicSubtitle = t("Anyone with a link has access.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_privateSubtitle = t("Only your students have access.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_classCodeCopied = t("Class code copied – you can paste it with Ctrl+V/Cmd+V.", {
        _tags: "SchoolClasses, ClassDetails",
    });
    const t_codeCopied = (name: string) =>
        t("Code from {name} copied – you can paste it with Ctrl+V/Cmd+V.", {
            _tags: "SchoolClasses, ClassDetails",
            name,
        });

    const classCode = schoolClass?.token ?? "";

    const classVisibility = schoolClass?.classVisibility ?? SchoolClassVisibilities.PUBLIC;

    const isPrivate = isPrivateSchoolClass(schoolClass?.classVisibility);

    const areClassMembersLoaded = classMembers !== null;
    const loadedClassMembers = useMemo(() => classMembers ?? [], [classMembers]);

    const reloadClassMembers = useCallback(async () => {
        try {
            const result = await getMembersForClass(schoolClassId);
            setClassMembers(result);
            setClassMembersLoadFailed(false);
        } catch (e) {
            console.error(e);
            setClassMembersLoadFailed(true);
            classMembersLoadErrorToast.triggerToast();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolClassId]);

    // A visibility flip invalidates the loaded list (the server drops members on private → public),
    // so reset before refetching — otherwise flipping back to private briefly shows the stale list.
    useEffect(() => {
        if (!isLoaded) return;
        setClassMembers(null);
        setClassMembersLoadFailed(false);
        if (isPrivate) {
            reloadClassMembers();
        }
    }, [isLoaded, isPrivate, reloadClassMembers]);

    // Classroom preview exits land here with ?exitPreview=1. The ClassMemberContextProvider is not
    // mounted under /klassen, so clear the session with the plain storage helper, then strip the
    // indicator so back/refresh can't replay the logout. Gated on isLoaded (teacher authenticated)
    // and on the stored session belonging to this class, so a drive-by visit to the URL can't wipe
    // an unrelated student's session. No persisted test data is cleared: preview blocks every
    // activity start, so a preview session can never write any.
    useEffect(() => {
        if (!isLoaded || !isExitPreviewSearch(search)) return;
        const storedSession = readClassMemberSession();
        if (storedSession?.member.schoolClassId.toUpperCase() === schoolClassId.toUpperCase()) {
            clearClassMemberSession();
        }
        const params = new URLSearchParams(search);
        params.delete(EXIT_PREVIEW_PARAM);
        history.replace({ pathname: getClassDetailsRoute(schoolClassId), search: params.toString() });
    }, [isLoaded, search, schoolClassId, history]);

    // Must never reject: the add popups rely on failedNames to surface errors, and a rejection
    // would escape their submit handlers as an unhandled promise rejection.
    const handleAddClassMembers = async (names: string[]): Promise<{ failedNames: string[] }> => {
        try {
            const { succeeded, failed } = await addMembersToClass(schoolClassId, names);
            if (succeeded.length > 0) {
                logEvent("added class members", {
                    class_id: classCode,
                    members_added_count: succeeded.length,
                    members_failed_count: failed.length,
                });
                classMembersAddedToast.triggerToast();
                await reloadClassMembers();
            }
            if (failed.length > 0) {
                classMembersAddErrorToast.triggerToast();
            }
            return { failedNames: failed };
        } catch (e) {
            console.error(e);
            classMembersAddErrorToast.triggerToast();
            return { failedNames: names };
        }
    };

    const handleEditMemberSubmit = async (newName: string) => {
        if (!editingMember) return;
        setIsDataBeingLoaded(true);
        try {
            await updateClassMember(editingMember.id, newName);
            setClassMembers(
                (prev) =>
                    prev &&
                    prev
                        .map((m) => (m.id === editingMember.id ? { ...m, name: newName } : m))
                        .sort((a, b) => a.name.localeCompare(b.name))
            );
            classMemberEditedToast.triggerToast();
            setEditingMember(null);
        } catch (e) {
            console.error(e);
            classMemberActionErrorToast.triggerToast();
        } finally {
            setIsDataBeingLoaded(false);
        }
    };

    const newMemberIds = useNewClassMembers(schoolClassId, classMembers);

    const duplicateGroups = useMemo(
        () =>
            findDuplicateGroups(loadedClassMembers).filter(
                (group) => !dismissedDuplicateKeys.includes(duplicateGroupKey(group))
            ),
        [loadedClassMembers, dismissedDuplicateKeys]
    );

    const loadResultsForClassTests = async (): Promise<Record<string, StudentTestAnswer[]>> => {
        const tests = await getUserTests();
        const classTestIds = tests
            .filter((test) => test.groupId === schoolClassId && test.id)
            .map((test) => test.id as string);
        const entries = await Promise.all(
            classTestIds.map(async (testId) => [testId, await getTestResultsForTest(testId)] as const)
        );
        return Object.fromEntries(entries);
    };

    const closeReview = () => {
        mergeResultsRef.current = null;
        setReview(null);
    };

    // Rethrows: returning false would let the popup proceed to merge.
    const handleCheckMergeConflicts = async (selected: ClassMember[]): Promise<boolean> => {
        try {
            const resultsByTest = await loadResultsForClassTests();
            const { hasConflicts } = planClassMemberMerge({
                selected,
                resultsByTest,
                strategy: MergeConflictStrategy.FIRST,
            });
            mergeResultsRef.current = { key: duplicateGroupKey(selected), resultsByTest, hasConflicts };
            return hasConflicts;
        } catch (e) {
            console.error(e);
            classMemberActionErrorToast.triggerToast();
            closeReview();
            throw e;
        }
    };

    const handleMergeMembers = async (selected: ClassMember[], strategy: MergeConflictStrategy) => {
        setIsDataBeingLoaded(true);
        try {
            const cached = mergeResultsRef.current;
            const isFresh = cached?.key === duplicateGroupKey(selected) && !cached.hasConflicts;
            const resultsByTest = isFresh ? cached.resultsByTest : await loadResultsForClassTests();
            const plan = planClassMemberMerge({ selected, resultsByTest, strategy });
            await executeClassMemberMerge(plan, {
                createResult: createClassMemberTestResult,
                saveResult: saveStudentTestResult,
                deleteMember: deleteClassMember,
            });
            logEvent("merged class members", {
                class_id: classCode,
                merged_count: selected.length,
                has_conflicts: plan.hasConflicts,
                source: review?.source,
                ...(plan.hasConflicts ? { conflict_strategy: strategy.toLowerCase() } : {}),
            });
            classMembersMergedToast.triggerToast();
        } catch (e) {
            console.error(e);
            classMemberActionErrorToast.triggerToast();
        } finally {
            closeReview();
            setIsDataBeingLoaded(false);
            await reloadClassMembers();
        }
    };

    const handleKeepSeparate = () => {
        if (!review) return;
        dismissDuplicateGroup(schoolClassId, review.members);
        setDismissedDuplicateKeys(readDismissedDuplicateKeys(schoolClassId));
        logEvent("kept duplicate class members", { class_id: classCode, group_size: review.members.length });
        closeReview();
    };

    // A failure midway keeps the undeleted members listed for a retry.
    const handleDeleteMembersConfirm = async () => {
        if (!deletingMembers) return;
        setIsDataBeingLoaded(true);
        const deletedIds = new Set<string>();
        try {
            for (const member of deletingMembers) {
                await deleteClassMember(member.id);
                deletedIds.add(member.id);
            }
            (deletingMembers.length === 1 ? classMemberDeletedToast : classMembersDeletedToast).triggerToast();
            setDeletingMembers(null);
        } catch (e) {
            console.error(e);
            classMemberActionErrorToast.triggerToast();
            setDeletingMembers((prev) => prev && prev.filter((m) => !deletedIds.has(m.id)));
        } finally {
            setClassMembers((prev) => prev && prev.filter((m) => !deletedIds.has(m.id)));
            setIsDataBeingLoaded(false);
        }
    };

    const handleEditSubmit = async (
        editedSchoolClassId: string,
        newName: string,
        newClassVisibility?: SchoolClassVisibilities
    ): Promise<boolean> => {
        const success = await updateSchoolClass(editedSchoolClassId, newName, newClassVisibility);
        if (success) {
            editSuccessToast.triggerToast();
        }
        return success;
    };

    const handleClassLinkCopied = () => {
        // Only private classes load a member list, so members_count is sent only when it is really
        // known — for a public class the list is never fetched and a 0 would read as "no students"
        // rather than "not applicable".
        logEvent("shared class access", {
            class_id: classCode,
            ...(isPrivate ? { members_count: loadedClassMembers.length } : {}),
            method: "link",
            class_visibility: toClassVisibilityProp(classVisibility),
        });
        linkCopiedToast.triggerToast();
    };

    // Copies from the single-student popup are still class access being shared — same event,
    // distinguished by method, so the funnel stays comparable across sharing routes.
    const handleMemberAccessCopied = (kind: ClassMemberCopyKind) => {
        logEvent("shared class access", {
            class_id: classCode,
            members_count: loadedClassMembers.length,
            method: ANALYTICS_METHOD_BY_COPY_KIND[kind],
            class_visibility: toClassVisibilityProp(classVisibility),
        });

        if (kind === ClassMemberCopyKind.DIRECT_LINK) {
            linkCopiedToast.triggerToast();
        } else {
            setCodeCopiedMessage(
                kind === ClassMemberCopyKind.CLASS_CODE ? t_classCodeCopied : t_codeCopied(sharingMember?.name ?? "")
            );
            codeCopiedToast.triggerToast();
        }
    };

    const handleCopyFailed = () => classMemberActionErrorToast.triggerToast();

    const handleDeleteConfirm = async () => {
        if (!schoolClass) return;
        setOpenPopup(OpenPopup.NONE);
        const success = await deleteSchoolClass(schoolClass.token);
        if (success) {
            history.push(getSchoolClassesRoute(), { showDeleteToast: true });
        }
    };

    const renderClassHeader = () => {
        if (!schoolClass) return null;

        const visibilityIcon = isPrivate ? (
            <PhaseSixIcon
                name="lock"
                className="text-xl"
            />
        ) : (
            <PhosphorIcon
                phosphorIcon={GlobeSimpleIcon}
                fontSize="text-xl"
                color="text-p6-grey-dark"
            />
        );

        return (
            // Desktop: mb-s + the parent's gap-s = 32px to the first card, vs 16px between the
            // cards. Mobile keeps a uniform 16px.
            <div className={`flex flex-col w-full ${isDesktop ? "mb-s" : ""}`}>
                <div className="flex items-start justify-between gap-s w-full">
                    <h1
                        className="text-xxl font-semibold text-p6-grey-dark min-w-0 [overflow-wrap:anywhere]"
                        data-testid="class-details-name"
                    >
                        {schoolClass.name}
                    </h1>
                    <div className="flex gap-xs items-center shrink-0">
                        <IconButton
                            data-testid="button-edit-class"
                            iconName="edit-avatar"
                            ariaLabel={t_sr_edit}
                            onClick={() => setOpenPopup(OpenPopup.EDIT)}
                            size="text-xxl"
                        />
                        <IconButton
                            data-testid="button-delete-class"
                            iconName="trash"
                            ariaLabel={t_sr_delete}
                            onClick={() => setOpenPopup(OpenPopup.DELETE)}
                            size="text-xxl"
                        />
                    </div>
                </div>
                <p
                    className="text-m text-p6-grey-dark w-full"
                    data-testid="class-details-visibility"
                    data-visibility={classVisibility}
                >
                    <span className="inline-flex align-middle mr-xs">{visibilityIcon}</span>
                    {isPrivate ? t_private : t_public} • {isPrivate ? t_privateSubtitle : t_publicSubtitle}
                </p>
            </div>
        );
    };

    if (!isLoaded) return null;

    return (
        <SchoolClassPageLayout
            backLabel={t_backToClasses}
            onBack={() => history.push(getSchoolClassesRoute())}
            data-testid="class-details-screen"
            bottomContent={
                schoolClass ? (
                    <ClassActivitiesSection
                        schoolClassId={schoolClassId}
                        schoolClassName={schoolClass.name}
                        classVisibility={classVisibility}
                        classMembers={loadedClassMembers}
                        areClassMembersLoaded={areClassMembersLoaded}
                        hasClassMembersLoadError={classMembersLoadFailed}
                        onAddClassMembers={() => setOpenPopup(OpenPopup.ADD_MEMBERS)}
                    />
                ) : undefined
            }
        >
            {schoolClass && (
                <div className="flex flex-col gap-s w-full">
                    {renderClassHeader()}

                    {isPrivate && (
                        <>
                            <ClassMembersSection
                                classMembers={loadedClassMembers}
                                isLoading={!areClassMembersLoaded && !classMembersLoadFailed}
                                hasLoadError={classMembersLoadFailed}
                                onAddClassMembers={() => setOpenPopup(OpenPopup.ADD_MEMBERS)}
                                onShareMemberAccess={setSharingMember}
                                onEditMember={setEditingMember}
                                onDeleteMember={(member) => setDeletingMembers([member])}
                                newMemberIds={newMemberIds}
                                duplicateGroups={duplicateGroups}
                                onReviewDuplicates={(group) => setReview({ members: group, source: "duplicate_group" })}
                                onMergeSelection={(selected) => setReview({ members: selected, source: "selection" })}
                                onDeleteSelection={setDeletingMembers}
                            />
                            <AddClassMembersPopup
                                isOpen={openPopup === OpenPopup.ADD_MEMBERS}
                                onClose={() => setOpenPopup(OpenPopup.NONE)}
                                onSubmit={handleAddClassMembers}
                                existingClassMemberNames={loadedClassMembers.map((s) => s.name)}
                            />
                        </>
                    )}

                    <ClassAccessSection
                        schoolClassId={schoolClassId}
                        classVisibility={classVisibility}
                        hasClassMembers={loadedClassMembers.length > 0}
                        areClassMembersLoaded={areClassMembersLoaded}
                        onShareClick={() => setOpenPopup(OpenPopup.SHARE)}
                    />
                </div>
            )}

            <ShareClassAccessPopup
                isOpen={openPopup === OpenPopup.SHARE}
                onClose={() => setOpenPopup(OpenPopup.NONE)}
                schoolClassId={schoolClassId}
                classVisibility={classVisibility}
                classMembersCount={loadedClassMembers.length}
                areClassMembersLoaded={areClassMembersLoaded}
                onDownloadCodes={() => history.push(getDownloadCodesRoute(schoolClassId))}
                onCopyLink={handleClassLinkCopied}
                onCopyFailed={handleCopyFailed}
            />

            {sharingMember && (
                <ShareClassMemberAccessPopup
                    key={sharingMember.id}
                    isOpen
                    onClose={() => setSharingMember(null)}
                    schoolClassId={schoolClassId}
                    classMember={sharingMember}
                    onCopied={handleMemberAccessCopied}
                    onCopyFailed={handleCopyFailed}
                />
            )}

            <EditClassPopup
                key={openPopup === OpenPopup.EDIT ? "open" : "closed"}
                schoolClass={openPopup === OpenPopup.EDIT ? schoolClass ?? null : null}
                onClose={() => setOpenPopup(OpenPopup.NONE)}
                onSubmit={handleEditSubmit}
                onCleanupPrivateToPublic={cleanupPrivateToPublic}
                existingClassNames={schoolClasses.map((c) => c.name)}
            />

            <DeleteClassPopup
                isOpen={openPopup === OpenPopup.DELETE}
                schoolClassName={schoolClass?.name ?? ""}
                onClose={() => setOpenPopup(OpenPopup.NONE)}
                onConfirm={handleDeleteConfirm}
            />

            {editingMember && (
                <EditClassMemberPopup
                    key={editingMember.id}
                    isOpen
                    currentName={editingMember.name}
                    otherMemberNames={loadedClassMembers.filter((m) => m.id !== editingMember.id).map((m) => m.name)}
                    onClose={() => setEditingMember(null)}
                    onSubmit={handleEditMemberSubmit}
                />
            )}

            {review && (
                <ReviewDuplicatesPopup
                    key={duplicateGroupKey(review.members)}
                    isOpen
                    group={review.members}
                    newMemberIds={newMemberIds}
                    onClose={closeReview}
                    onKeepSeparate={review.source === "selection" ? undefined : handleKeepSeparate}
                    hasMergeConflicts={handleCheckMergeConflicts}
                    onMerge={handleMergeMembers}
                />
            )}

            {deletingMembers && (
                <DeleteClassMemberPopup
                    key={duplicateGroupKey(deletingMembers)}
                    isOpen
                    classMemberNames={deletingMembers.map((m) => m.name)}
                    onClose={() => setDeletingMembers(null)}
                    onConfirm={handleDeleteMembersConfirm}
                />
            )}

            <ToastMessage
                text={t_linkCopied}
                duration={7000}
                isOpen={linkCopiedToast.isOpen}
                onOpenChange={linkCopiedToast.onOpenChange}
            />

            <ToastMessage
                text={t_classMembersAdded}
                isOpen={classMembersAddedToast.isOpen}
                onOpenChange={classMembersAddedToast.onOpenChange}
            />

            <ToastMessage
                text={t_classMembersAddError}
                duration={5000}
                variant={ToastVariants.ERROR}
                isOpen={classMembersAddErrorToast.isOpen}
                onOpenChange={classMembersAddErrorToast.onOpenChange}
            />

            <ToastMessage
                text={t_classMembersLoadError}
                duration={5000}
                variant={ToastVariants.ERROR}
                isOpen={classMembersLoadErrorToast.isOpen}
                onOpenChange={classMembersLoadErrorToast.onOpenChange}
            />

            <ToastMessage
                text={codeCopiedMessage}
                duration={7000}
                isOpen={codeCopiedToast.isOpen}
                onOpenChange={codeCopiedToast.onOpenChange}
            />

            <ToastMessage
                text={t_editSuccess}
                isOpen={editSuccessToast.isOpen}
                onOpenChange={editSuccessToast.onOpenChange}
            />

            <ToastMessage
                text={t_classMemberEdited}
                isOpen={classMemberEditedToast.isOpen}
                onOpenChange={classMemberEditedToast.onOpenChange}
            />

            <ToastMessage
                text={t_classMemberDeleted}
                isOpen={classMemberDeletedToast.isOpen}
                onOpenChange={classMemberDeletedToast.onOpenChange}
            />

            <ToastMessage
                text={t_classMembersDeleted}
                isOpen={classMembersDeletedToast.isOpen}
                onOpenChange={classMembersDeletedToast.onOpenChange}
            />

            <ToastMessage
                text={t_classMembersMerged}
                isOpen={classMembersMergedToast.isOpen}
                onOpenChange={classMembersMergedToast.onOpenChange}
                data-testid="toast-class-members-merged"
            />

            <ToastMessage
                text={t_classMemberActionError}
                duration={5000}
                variant={ToastVariants.ERROR}
                isOpen={classMemberActionErrorToast.isOpen}
                onOpenChange={classMemberActionErrorToast.onOpenChange}
            />
        </SchoolClassPageLayout>
    );
};

export default ClassDetails;
